import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { usePlan } from "../../context/PlanContext";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PromoBanner from "../../components/PromoBanner";
import {
  ArrowBigUp, ArrowBigDown, MessageSquare, Eye,
  Send, Flag, AlertTriangle, ArrowLeft, Reply, GitFork, Lock,
} from "lucide-react";
import { toast } from "sonner";

function timeAgo(ts) {
  if (!ts) return "";
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function VoteButtons({ id, score, onVote }) {
  const [busy, setBusy] = useState(false);
  const [vote, setVote] = useState(0);
  const [displayScore, setDisplayScore] = useState(score);
  useEffect(() => setDisplayScore(score), [score]);
  const doVote = async (dir) => {
    if (busy) return; setBusy(true);
    try {
      const res = await onVote(dir === vote ? 0 : dir);
      setVote(res.your_vote);
      setDisplayScore(res.score);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vote failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col items-center gap-0.5 pt-0.5">
      <button onClick={() => doVote(1)}
              className={`p-0.5 ${vote === 1 ? "text-[var(--bull)]" : "text-white/40 hover:text-[var(--bull)]"}`}
              data-testid={`vote-up-${id}`}>
        <ArrowBigUp className="w-4 h-4" fill={vote === 1 ? "currentColor" : "none"} />
      </button>
      <div className="font-mono text-[11px] tabular-nums font-semibold text-white/85">{displayScore}</div>
      <button onClick={() => doVote(-1)}
              className={`p-0.5 ${vote === -1 ? "text-[var(--bear)]" : "text-white/40 hover:text-[var(--bear)]"}`}
              data-testid={`vote-down-${id}`}>
        <ArrowBigDown className="w-4 h-4" fill={vote === -1 ? "currentColor" : "none"} />
      </button>
    </div>
  );
}

function flattenComments(all) {
  // Build tree, then depth-first flatten so we can render iteratively (avoids
  // babel stack-overflow that self-referencing JSX components sometimes hit).
  const byParent = new Map();
  for (const c of all) {
    const p = c.parent_id || "_root";
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(c);
  }
  const out = [];
  function walk(parent, depth) {
    const kids = byParent.get(parent) || [];
    for (const c of kids) {
      out.push({ ...c, _depth: Math.min(6, depth) });
      walk(c.id, depth + 1);
    }
  }
  walk("_root", 0);
  return out;
}

function CommentRow({ c, postId, onSubmit }) {
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const submitReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await onSubmit(reply.trim(), c.id);
      setReply(""); setReplying(false);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Reply failed");
    } finally { setSending(false); }
  };
  const isRemoved = c.mod_status === "removed" || c.body === "[deleted]";
  return (
    <div style={{ paddingLeft: c._depth * 14, borderLeft: c._depth > 0 ? "1px solid rgba(255,255,255,0.06)" : undefined,
                  marginLeft: c._depth > 0 ? 4 : 0 }}>
      <div className="flex gap-2 py-2" data-testid={`comment-${c.id}`}>
        <VoteButtons
          id={c.id}
          score={c.score || 0}
          onVote={async (dir) => (await api.post(`/api/community/comments/${c.id}/vote`, { direction: dir })).data}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] label-mono">
            {c.author_avatar_url && (
              <img src={c.author_avatar_url} alt="" className="w-4 h-4 rounded-full" loading="lazy" />
            )}
            <Link to={`/community/u/${c.author_username}`}
                  className="text-white/80 hover:text-white font-semibold">
              @{c.author_username}
            </Link>
            <span>·</span>
            <span>{timeAgo(c.created_at)}</span>
            {c.mod_status === "reduced_visibility" && (
              <span className="ml-1 text-[#ffd166]">reduced</span>
            )}
          </div>
          <div className={`mt-1 text-[13px] whitespace-pre-wrap leading-relaxed ${
            isRemoved ? "italic text-white/40" : "text-white/85"
          }`}>
            {isRemoved ? "[removed]" : c.body}
          </div>
          <div className="mt-1 flex items-center gap-3 label-mono">
            <button onClick={() => setReplying((r) => !r)}
                    className="inline-flex items-center gap-1 hover:text-white transition-colors"
                    data-testid={`reply-${c.id}`}>
              <Reply className="w-3 h-3" /> Reply
            </button>
          </div>
          {replying && (
            <div className="mt-2 flex gap-2">
              <input value={reply} onChange={(e) => setReply(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && submitReply()}
                     className="input-glass flex-1 text-[13px]" placeholder="Write a reply…"
                     data-testid={`reply-input-${c.id}`} autoFocus />
              <button onClick={submitReply} disabled={sending || !reply.trim()}
                      className="btn-accent" data-testid={`reply-submit-${c.id}`}>
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PostDetail() {
  const { post_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan, showUpgrade } = usePlan();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [forking, setForking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/api/community/posts/${post_id}`);
      setPost(data.post);
      setComments(data.comments || []);
    } catch (err) {
      toast.error("Post not found");
      navigate("/community");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [post_id]);

  const submitComment = async (body, parentId = null) => {
    setPosting(true);
    try {
      await api.post("/api/community/comments", {
        post_id, parent_id: parentId, body,
      });
      if (!parentId) setNewComment("");
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || d || "Comment failed");
      throw err;
    } finally { setPosting(false); }
  };

  const votePost = async (dir) => (await api.post(`/api/community/posts/${post_id}/vote`, { direction: dir })).data;

  const fork = async () => {
    if (!user) { navigate("/login"); return; }
    if (plan !== "executive") { showUpgrade("fork_gated"); return; }
    setForking(true);
    try {
      const { data } = await api.post(`/api/community/posts/${post_id}/fork`);
      toast.success(`Forked @${data.author_username}'s setup — applying to your scanner`);
      // Deep-link to Dashboard with fork param; Dashboard reads it and applies filters
      navigate(`/dashboard?fork=${post_id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "fork_gated") {
        showUpgrade("fork_gated");
      } else {
        toast.error(d?.message || d || "Fork failed");
      }
    } finally { setForking(false); }
  };

  const topComments = useMemo(() => flattenComments(comments), [comments]);

  if (!post) return (
    <div className="min-h-screen flex flex-col">
      <Header /><PromoBanner />
      <main id="main-content" className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-6">
        <div className="text-white/50 text-sm text-center py-10">Loading post…</div>
      </main>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col" data-testid="post-detail-page">
      <Header />
      <PromoBanner />
      <main id="main-content" className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate(-1)}
                className="btn-ghost inline-flex items-center gap-1 mb-3 text-[12px]"
                data-testid="back-btn">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>

        <div className="card-surface p-5">
          <div className="flex gap-3">
            <VoteButtons id={post.id} score={post.score || 0} onVote={votePost} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap label-mono">
                <Link to={`/community/${post.community_slug}`}
                      className="text-white/80 hover:text-white font-semibold">
                  r/{post.community_slug}
                </Link>
                <span>·</span>
                <span>Posted by</span>
                <Link to={`/community/u/${post.author_username}`}
                      className="text-white/80 hover:text-white inline-flex items-center gap-1">
                  {post.author_avatar_url && (
                    <img src={post.author_avatar_url} alt="" className="w-4 h-4 rounded-full" />
                  )}
                  @{post.author_username}
                </Link>
                <span>·</span>
                <span>{timeAgo(post.created_at)}</span>
                {(post.ticker_tags || []).map((t) => (
                  <span key={`pt-${t}`}
                        className="px-1.5 py-0.5 rounded bg-[var(--bull)]/15 text-[var(--bull)] font-mono text-[10px]">
                    ${t}
                  </span>
                ))}
              </div>
              <h1 className="mt-2 text-xl sm:text-2xl text-white font-semibold tracking-tight leading-tight">
                {post.title}
              </h1>
              {post.body && (
                <div className="mt-3 text-white/85 text-[14px] leading-relaxed whitespace-pre-wrap">
                  {post.body}
                </div>
              )}
              {post.source === "scanner_share" && (
                <div className="mt-4 p-3 rounded-2xl border border-[var(--bull)]/25 bg-[var(--bull)]/[0.05] flex items-center justify-between gap-3 flex-wrap"
                     data-testid="fork-cta-block">
                  <div className="flex items-center gap-2 min-w-0">
                    <GitFork className="w-4 h-4 text-[var(--bull)] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-white text-[13px] font-semibold tracking-tight">
                        Shared scan setup
                      </div>
                      <div className="label-mono">
                        {post.fork_count || 0} fork{post.fork_count === 1 ? "" : "s"} · one click applies these filters to your scanner
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={fork}
                    disabled={forking || (user && post.user_id === user.user_id)}
                    className={`btn-accent inline-flex items-center gap-2 ${
                      user && post.user_id === user.user_id ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    data-testid="fork-btn"
                    title={user && post.user_id === user.user_id ? "You can't fork your own setup" : "Copy these filters into my scanner"}
                  >
                    {plan !== "executive" ? <Lock className="w-3.5 h-3.5" /> : <GitFork className="w-3.5 h-3.5" />}
                    {forking ? "Forking…" : "Fork this setup"}
                  </button>
                </div>
              )}
              <div className="mt-4 flex items-center gap-4 label-mono">
                <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {post.comment_count || 0}</span>
                <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {post.view_count || 0}</span>
                {post.mod_status === "reduced_visibility" && (
                  <span className="text-[#ffd166] inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Reduced visibility
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Comment composer */}
        {user && (
          <div className="card-surface p-3 mt-3" data-testid="comment-composer">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment"
              rows={3}
              className="w-full bg-transparent text-white text-[14px] placeholder:text-white/30 focus:outline-none resize-none"
              data-testid="new-comment-input"
            />
            <div className="flex items-center justify-end mt-1">
              <button
                disabled={!newComment.trim() || posting}
                onClick={() => submitComment(newComment.trim())}
                className="btn-accent inline-flex items-center gap-2"
                data-testid="submit-comment-btn"
              >
                <Send className="w-3.5 h-3.5" /> Comment
              </button>
            </div>
          </div>
        )}

        {/* Comments */}
        <div className="mt-4">
          <div className="label-mono mb-2">Comments ({comments.length})</div>
          {topComments.length === 0 && (
            <div className="text-white/40 text-sm text-center py-6">Be the first to comment</div>
          )}
          {topComments.map((c) => (
            <CommentRow key={c.id} c={c} postId={post_id} onSubmit={submitComment} />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
